/**
 * Client typé pour les routes `/auth/*` — Phase 4, incrément 4.3.
 *
 * Reflète la surface de `apps/api/src/routes/auth.ts`. Les codes
 * erreur (`invalid_credentials`, `email_taken`, etc.) sont
 * machine-readable — c'est l'UI qui traduit côté `t()` selon la
 * langue de l'utilisateur (CLAUDE.md §1.8).
 *
 * Tous les appels passent par `apiRequest` qui inclut
 * automatiquement les cookies (httpOnly `nestor_session`).
 */
import { apiRequest } from './client.js';

/**
 * Codes ISO 639-1 supportés par l'API. Doit rester aligné avec
 * `SUPPORTED_LANGS` côté backend (`apps/api/src/routes/auth.ts`).
 */
export type AuthLang = 'fr' | 'en' | 'es' | 'de' | 'it' | 'pt' | 'pl';

/**
 * Utilisateur public — ce que l'API expose. Pas de `passwordHash`,
 * pas de timestamps internes.
 */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  lang: AuthLang;
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
  lang?: AuthLang;
}

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * Crée un nouveau compte + pose le cookie de session. Retourne
 * l'utilisateur créé. `ApiError` 409 `email_taken` si email dupliqué.
 */
export async function register(input: RegisterInput): Promise<AuthUser> {
  const data = await apiRequest<{ user: AuthUser }>('/auth/register', {
    method: 'POST',
    body: input,
  });
  return data.user;
}

/**
 * Authentifie + pose le cookie de session. `ApiError` 401
 * `invalid_credentials` sur mauvais email ou password.
 */
export async function login(input: LoginInput): Promise<AuthUser> {
  const data = await apiRequest<{ user: AuthUser }>('/auth/login', {
    method: 'POST',
    body: input,
  });
  return data.user;
}

/**
 * Clear le cookie côté navigateur. Toujours OK (200) même si pas
 * de session active. Ne throw qu'en cas d'erreur réseau.
 */
/**
 * Supprime définitivement le compte serveur (RGPD, Phase 9B) : le
 * serveur efface l'utilisateur + ses foyers dont il est seul membre,
 * et invalide le cookie de session.
 */
export async function deleteAccount(): Promise<void> {
  await apiRequest<{ ok: true }>('/auth/me', { method: 'DELETE' });
}

export async function logout(): Promise<void> {
  await apiRequest<{ ok: true }>('/auth/logout', { method: 'POST' });
}

/**
 * Retourne l'utilisateur courant si la session est valide.
 * `ApiError` 401 `unauthorized` si pas de cookie ou cookie invalide.
 */
export async function me(): Promise<AuthUser> {
  const data = await apiRequest<{ user: AuthUser }>('/auth/me');
  return data.user;
}
